import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { 
  ShoppingCart, ArrowLeft, ChevronLeft, ChevronRight, 
  Package, Sparkles, Minus, Plus, Check, AlertTriangle, Gift, ZoomIn, X, Truck, MapPin
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Separator } from "./ui/separator";
import { Input } from "./ui/input";
import RelatedProducts from "./RelatedProducts";
import MarkdownLite, { stripMarkdown } from "./MarkdownLite";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_SITE_URL || "https://www.bilan2vie.fr";

export default function ProductDetailPage({ useCart }) {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [giftAmount, setGiftAmount] = useState(null);
  const [customGiftAmount, setCustomGiftAmount] = useState('');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API}/products/${productId}`);
        setProduct(res.data);
        if (res.data.variations?.length > 0) {
          const inStock = res.data.variations.find(v => v.stock > 0);
          setSelectedVariation(inStock || res.data.variations[0]);
        }
      } catch (error) {
        toast.error("Produit introuvable");
        navigate('/boutique');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    window.scrollTo(0, 0);
  }, [productId, navigate]);

  // Navigation clavier de la lightbox — placé AVANT les early returns (règle des Hooks).
  useEffect(() => {
    if (!lightboxOpen) return;
    const imgs = selectedVariation?.images?.length > 0 ? selectedVariation.images
      : (product?.images?.length > 0 ? product.images : (product?.image_url ? [product.image_url] : []));
    const n = imgs.length;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      else if (e.key === 'ArrowRight' && n > 1) setSelectedImageIndex(i => (i + 1) % n);
      else if (e.key === 'ArrowLeft' && n > 1) setSelectedImageIndex(i => (i - 1 + n) % n);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [lightboxOpen, product, selectedVariation]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1B132B] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#D926A9] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) return null;

  // Images: si la variation sélectionnée a ses propres images, les utiliser
  const allImages = product.images?.length > 0 ? product.images : (product.image_url ? [product.image_url] : []);
  const variationImages = selectedVariation?.images?.length > 0 ? selectedVariation.images : null;
  const images = variationImages || allImages;
  const hasVariations = product.has_variations && product.variations?.length > 1;
  const currentPrice = selectedVariation?.price || product.price || 0;
  const currentStock = selectedVariation ? Math.max(0, selectedVariation.stock ?? 0) : Math.max(0, product.stock ?? 0);
  const totalStock = Math.max(0, product.stock ?? 0);

  const handleAddToCart = () => {
    if (product.is_gift_card) {
      const amount = giftAmount || parseFloat(customGiftAmount);
      if (!amount || amount < (product.min_amount || 10) || amount > (product.max_amount || 500)) {
        toast.error(`Choisis un montant entre ${product.min_amount || 10}€ et ${product.max_amount || 500}€`);
        return;
      }
      const cartItem = { ...product, quantity: 1 };
      addToCart(cartItem, amount);
      return;
    }
    const cartItem = {
      ...product,
      price: currentPrice,
      selected_variation: selectedVariation ? {
        variation_id: selectedVariation.variation_id,
        name: selectedVariation.name,
        price: selectedVariation.price
      } : null,
      quantity
    };
    for (let i = 0; i < quantity; i++) {
      addToCart(cartItem);
    }
    toast.success(`${product.name}${selectedVariation && hasVariations ? ` — ${selectedVariation.name}` : ''} ajouté au panier`);
  };

  const nextImage = () => setSelectedImageIndex(i => (i + 1) % images.length);
  const prevImage = () => setSelectedImageIndex(i => (i - 1 + images.length) % images.length);

  // SEO : title, meta description, canonical + données structurées Product (schema.org)
  const canonicalUrl = `${SITE_URL}/boutique/${product.product_id}`;
  const seoDesc = (stripMarkdown(product.short_description || product.description || '') || `${product.name} — boutique spirituelle Bilan 2 Vie à Montélimar.`).slice(0, 160);
  const productJsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "image": images.length > 0 ? images : undefined,
    "description": seoDesc,
    "category": product.category || undefined,
    "brand": { "@type": "Brand", "name": "Bilan 2 Vie" },
    "offers": {
      "@type": "Offer",
      "url": canonicalUrl,
      "priceCurrency": "EUR",
      "price": Number(currentPrice || 0).toFixed(2),
      "availability": currentStock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "itemCondition": "https://schema.org/NewCondition"
    }
  };

  return (
    <div className="min-h-screen bg-[#1B132B] pt-24 pb-16" data-testid="product-detail-page">
      <Helmet>
        <title>{`${product.name} | Bilan 2 Vie Montélimar`}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} | Bilan 2 Vie Montélimar`} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={canonicalUrl} />
        {images.length > 0 && <meta property="og:image" content={images[0]} />}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-8 text-sm" data-testid="product-breadcrumb">
          <Link to="/boutique" className="text-[#B59ED1] hover:text-[#00E5FF] transition-colors flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Boutique
          </Link>
          <span className="text-[#B59ED1]/50">/</span>
          <span className="text-[#E2D5F8]">{product.category}</span>
          <span className="text-[#B59ED1]/50">/</span>
          <span className="text-[#E2D5F8]/60 truncate max-w-[200px]">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Image Gallery */}
          <div className="space-y-4" data-testid="product-gallery">
            {/* Main Image */}
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#261B3B] border border-white/15">
              <AnimatePresence mode="wait">
                {images.length > 0 ? (
                  <motion.img
                    key={selectedImageIndex}
                    src={images[selectedImageIndex]}
                    alt={product.name}
                    className="w-full h-full object-cover cursor-zoom-in"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => setLightboxOpen(true)}
                    onError={(e) => { e.target.src = 'https://images.pexels.com/photos/6944752/pexels-photo-6944752.jpeg'; }}
                    data-testid="product-main-image"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Sparkles className="w-20 h-20 text-[#D926A9]/40" />
                  </div>
                )}
              </AnimatePresence>

              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/70 transition-all"
                    data-testid="gallery-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/70 transition-all"
                    data-testid="gallery-next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-xs text-white/80">
                    {selectedImageIndex + 1} / {images.length}
                  </div>
                </>
              )}

              {/* Out of stock overlay */}
              {totalStock === 0 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white text-xl font-medium bg-black/60 px-6 py-3 rounded-full">Rupture de stock</span>
                </div>
              )}

              {/* Indice "cliquer pour agrandir" */}
              {images.length > 0 && (
                <button
                  onClick={() => setLightboxOpen(true)}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/90 hover:bg-black/70 transition-all"
                  title="Agrandir la photo"
                  data-testid="gallery-zoom"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {images.map((img, idx) => (
                  <button
                    key={img || idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === selectedImageIndex 
                        ? 'border-[#D926A9] ring-1 ring-[#D926A9]/50' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" 
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Lightbox plein écran */}
          <AnimatePresence>
            {lightboxOpen && images.length > 0 && (
              <motion.div
                className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                data-testid="product-lightbox"
                onClick={() => setLightboxOpen(false)}
              >
                {/* Barre haut : compteur + fermer */}
                <div className="flex items-center justify-between p-4 text-white/80" onClick={(e) => e.stopPropagation()}>
                  <span className="text-sm">{selectedImageIndex + 1} / {images.length}</span>
                  <button
                    onClick={() => setLightboxOpen(false)}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
                    title="Fermer" data-testid="lightbox-close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Image + flèches */}
                <div className="relative flex-1 flex items-center justify-center px-4 min-h-0" onClick={(e) => e.stopPropagation()}>
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={selectedImageIndex}
                      src={images[selectedImageIndex]}
                      alt={product.name}
                      className="max-w-full max-h-full object-contain select-none"
                      initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      onError={(e) => { e.target.src = 'https://images.pexels.com/photos/6944752/pexels-photo-6944752.jpeg'; }}
                      data-testid="lightbox-image"
                    />
                  </AnimatePresence>
                  {images.length > 1 && (
                    <>
                      <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all" data-testid="lightbox-prev">
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all" data-testid="lightbox-next">
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}
                </div>

                {/* Miniatures défilantes */}
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto p-4 justify-start sm:justify-center scrollbar-hide" onClick={(e) => e.stopPropagation()}>
                    {images.map((img, idx) => (
                      <button
                        key={img || idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-all ${idx === selectedImageIndex ? 'border-[#D926A9] ring-1 ring-[#D926A9]/50' : 'border-white/20 opacity-60 hover:opacity-100'}`}
                        data-testid={`lightbox-thumbnail-${idx}`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Product Info */}
          <div className="space-y-6" data-testid="product-info">
            <div>
              <Badge className="bg-[#3A115E] text-[#E2D5F8] mb-3">{product.category}</Badge>
              <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2" data-testid="product-name">
                {product.name}
              </h1>
              <div className="flex items-center gap-3">
                {product.is_gift_card ? (
                  <span className="text-xl font-serif text-[#D926A9]" data-testid="product-price">
                    À partir de {product.min_amount || 10} €
                  </span>
                ) : (
                  <span className="text-3xl font-serif text-[#D926A9]" data-testid="product-price">
                    {currentPrice.toFixed(2)} €
                  </span>
                )}
                {hasVariations && selectedVariation && !product.is_gift_card && (
                  <span className="text-sm text-[#B59ED1]">
                    — {selectedVariation.name}
                  </span>
                )}
              </div>
            </div>

            <Separator className="bg-white/10" />

            {/* Variation Selector */}
            {hasVariations && !product.is_gift_card && (
              <div className="space-y-3" data-testid="variation-selector">
                <label className="text-sm font-medium text-[#E2D5F8]">
                  Choisir une déclinaison
                </label>
                <Select
                  value={selectedVariation?.variation_id || ""}
                  onValueChange={(val) => {
                    const v = product.variations.find(v => v.variation_id === val);
                    if (v) {
                      setSelectedVariation(v);
                      setSelectedImageIndex(0);
                      setQuantity(1);
                    }
                  }}
                >
                  <SelectTrigger 
                    className="w-full bg-[#261B3B] border-white/10 text-white h-12"
                    data-testid="variation-dropdown"
                  >
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#261B3B] border-white/10">
                    {product.variations.map((v) => (
                      <SelectItem 
                        key={v.variation_id} 
                        value={v.variation_id}
                        className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white"
                        data-testid={`variation-option-${v.variation_id}`}
                      >
                        <div className="flex items-center justify-between w-full gap-4">
                          <span>{v.name}</span>
                          <span className="flex items-center gap-2 text-xs">
                            <span className="text-[#D926A9]">{v.price.toFixed(2)} €</span>
                            {v.stock > 0 ? (
                              <span className="text-emerald-400">En stock ({v.stock})</span>
                            ) : (
                              <span className="text-red-400">Rupture</span>
                            )}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Stock Status */}
            {!product.is_gift_card && (
            <div className="flex items-center gap-2" data-testid="stock-status">
              {currentStock > 0 ? (
                <>
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 text-sm">En stock ({currentStock} disponible{currentStock > 1 ? 's' : ''})</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-sm">Rupture de stock</span>
                </>
              )}
            </div>
            )}

            {/* Gift Card Amount Selector */}
            {product.is_gift_card && (
              <div className="space-y-4" data-testid="gift-card-selector">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5 text-[#D926A9]" />
                  <h3 className="font-serif text-lg">Choisis le montant</h3>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(product.preset_amounts || [20, 30, 50, 75, 100, 150]).map(amount => (
                    <button
                      key={amount}
                      onClick={() => { setGiftAmount(amount); setCustomGiftAmount(''); }}
                      className={`py-3 rounded-lg text-sm font-semibold transition-all ${giftAmount === amount ? 'bg-[#D926A9] text-white' : 'bg-[#261B3B] border border-white/10 text-[#E2D5F8] hover:border-[#D926A9]/40'}`}
                      data-testid={`gift-amount-${amount}`}
                    >
                      {amount} €
                    </button>
                  ))}
                </div>
                <div>
                  <label className="text-sm text-[#B59ED1] mb-1 block">Ou saisissez un montant personnalisé</label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={customGiftAmount}
                      onChange={(e) => { setCustomGiftAmount(e.target.value); setGiftAmount(null); }}
                      placeholder={`${product.min_amount || 10} — ${product.max_amount || 500}`}
                      min={product.min_amount || 10}
                      max={product.max_amount || 500}
                      className="bg-[#261B3B] border-white/10 flex-1"
                      data-testid="gift-custom-amount"
                    />
                    <span className="text-[#B59ED1]">€</span>
                  </div>
                </div>
                {(giftAmount || customGiftAmount) && (
                  <div className="text-center py-2">
                    <span className="text-3xl font-serif text-[#D926A9]">{giftAmount || customGiftAmount} €</span>
                  </div>
                )}
              </div>
            )}

            {/* Quantity + Add to Cart */}
            <div className="flex items-center gap-4" data-testid="add-to-cart-section">
              {!product.is_gift_card && (
                <div className="flex items-center bg-[#261B3B] rounded-lg border border-white/10">
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="p-3 text-white/60 hover:text-white transition-colors"
                    disabled={quantity <= 1}
                    data-testid="qty-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-4 text-white font-medium min-w-[40px] text-center" data-testid="qty-value">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(q => Math.min(currentStock, q + 1))}
                    className="p-3 text-white/60 hover:text-white transition-colors"
                    disabled={quantity >= currentStock}
                    data-testid="qty-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
              <Button
                onClick={handleAddToCart}
                disabled={product.is_gift_card ? (!giftAmount && !customGiftAmount) : currentStock === 0}
                className="flex-1 gradient-primary h-12 text-base gap-2"
                data-testid="add-to-cart-btn"
              >
                {product.is_gift_card ? (
                  <><Gift className="w-5 h-5" />Offrir cette carte cadeau</>
                ) : (
                  <><ShoppingCart className="w-5 h-5" />Ajouter au panier</>
                )}
              </Button>
            </div>

            <Separator className="bg-white/10" />

            {/* Description */}
            <div className="space-y-3" data-testid="product-description">
              <h2 className="text-lg font-serif text-white">Description</h2>
              <MarkdownLite content={product.description} />
            </div>

            {/* Shipping Info */}
            <div className="bg-[#261B3B] rounded-xl p-5 space-y-3 border border-white/15" data-testid="shipping-reassurance">
              <div className="flex items-start gap-3 text-sm">
                <Truck className="w-4 h-4 text-[#00E5FF] mt-0.5 shrink-0" />
                <span className="text-[#E2D5F8]">Livraison en <strong className="text-white">point relais Mondial Relay dès 3,99&nbsp;€</strong> — ou à domicile.</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 text-[#FFD700] mt-0.5 shrink-0" />
                <span className="text-[#E2D5F8]"><strong className="text-white">Retrait gratuit en boutique</strong> à Montélimar (Click &amp; Collect).</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Gift className="w-4 h-4 text-[#D926A9] mt-0.5 shrink-0" />
                <span className="text-[#E2D5F8]"><strong className="text-white">Livraison offerte dès 80&nbsp;€</strong> d'achat en point relais.</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span className="text-[#E2D5F8]">Expédié sous 24-48h depuis ma boutique française · paiement sécurisé.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Produits complémentaires — suggestions autonomes */}
        <RelatedProducts currentProduct={product} useCart={useCart} />
      </div>
    </div>
  );
}
